import { useTheme } from "next-themes"
import { Toaster as Sonner, ToasterProps } from "sonner"
import { useEffect } from "react"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  // Ensure toast container has higher z-index than modals
  useEffect(() => {
    const styleId = 'sonner-z-index-fix'
    // Remove existing style if present
    const existingStyle = document.getElementById(styleId)
    if (existingStyle) {
      existingStyle.remove()
    }

    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      [data-sonner-toaster] {
        z-index: 999999 !important;
        position: fixed !important;
      }
      .sonner-toaster {
        z-index: 999999 !important;
        position: fixed !important;
      }
      .sonner-toast {
        z-index: 999999 !important;
        position: relative !important;
      }
      .sonner-toast-group {
        z-index: 999999 !important;
        position: relative !important;
      }
      div[class*="sonner"] {
        z-index: 999999 !important;
      }
      [data-sonner-toast] {
        z-index: 999999 !important;
      }
      [data-sonner-toast-wrapper] {
        z-index: 999999 !important;
      }
      .sonner-toaster-high-z {
        z-index: 999999 !important;
        position: fixed !important;
      }
      .sonner-toast-high-z {
        z-index: 999999 !important;
        position: relative !important;
      }
      [data-sonner-toast],
      [data-sonner-toast] *,
      .sonner-toast,
      .sonner-toast * {
        box-shadow: none !important;
      }
    `
    document.head.appendChild(style)
    
    // Also try to update any existing toaster elements
    const updateExistingToasters = () => {
      const toasters = document.querySelectorAll('[data-sonner-toaster], .sonner-toaster')
      toasters.forEach((el: any) => {
        if (el) {
          el.style.zIndex = '999999'
          el.style.position = 'fixed'
        }
      })
      
      // Also update toast elements
      const toastElements = document.querySelectorAll('[data-sonner-toast], [data-sonner-toast-wrapper]')
      toastElements.forEach((el: any) => {
        if (el) {
          el.style.zIndex = '999999'
          el.style.boxShadow = 'none'
        }
      })
    }
    
    // Update immediately and after a short delay
    updateExistingToasters()
    const timeout = setTimeout(updateExistingToasters, 100)
    
    // Watch for new toast elements being added to the DOM
    const observer = new MutationObserver(() => {
      updateExistingToasters()
      // Also update toast elements
      const toastElements = document.querySelectorAll('[data-sonner-toast], [data-sonner-toast-wrapper]')
      toastElements.forEach((el: any) => {
        if (el) {
          el.style.zIndex = '999999'
          el.style.boxShadow = 'none'
        }
      })
    })
    
    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
    
    return () => {
      clearTimeout(timeout)
      observer.disconnect()
      const styleElement = document.getElementById(styleId)
      if (styleElement) {
        styleElement.remove()
      }
    }
  }, [])

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group sonner-toaster-high-z"
      style={
        {
          "--normal-bg": "linear-gradient(to right, rgb(207, 250, 254), rgb(219, 234, 254))",
          "--normal-text": "rgb(17, 24, 39)",
          "--normal-border": "rgb(165, 243, 252)",
          "--success-bg": "linear-gradient(to right, rgb(240, 253, 244), rgb(236, 253, 245))",
          "--success-text": "rgb(20, 83, 45)",
          "--success-border": "rgb(134, 239, 172)",
          "--error-bg": "linear-gradient(to right, rgb(254, 242, 242), rgb(255, 247, 237))",
          "--error-text": "rgb(153, 27, 27)",
          "--error-border": "rgb(252, 165, 165)",
          "--warning-bg": "linear-gradient(to right, rgb(255, 251, 235), rgb(254, 243, 199))",
          "--warning-text": "rgb(146, 64, 14)",
          "--warning-border": "rgb(251, 191, 36)",
          "--info-bg": "linear-gradient(to right, rgb(207, 250, 254), rgb(219, 234, 254))",
          "--info-text": "rgb(22, 78, 99)",
          "--info-border": "rgb(103, 232, 249)",
        } as React.CSSProperties
      }
      toastOptions={{
        style: {
          zIndex: 999999,
          borderRadius: "0.5rem",
          boxShadow: "none",
          border: "1px solid",
          backdropFilter: "blur(8px)",
        },
        className: 'sonner-toast-high-z',
        classNames: {
          toast: "rounded-lg backdrop-blur-sm border",
          success: "bg-gradient-to-r from-green-50 dark:from-green-950/30 to-emerald-50 dark:to-emerald-950/30 border-green-300 text-green-900 dark:text-green-100",
          error: "bg-gradient-to-r from-red-50 dark:from-red-950/30 to-rose-50 dark:to-rose-950/30 border-red-300 text-red-900 dark:text-red-100",
          warning: "bg-gradient-to-r from-amber-50 dark:from-amber-950/30 to-yellow-50 dark:to-yellow-950/30 border-amber-300 text-amber-900 dark:text-amber-100",
          info: "bg-gradient-to-r from-emerald-50 dark:from-emerald-950/30 to-amber-50 dark:to-amber-950/30 border-emerald-300 text-emerald-900 dark:text-emerald-100",
          default: "bg-gradient-to-r from-emerald-50 dark:from-emerald-950/30 to-amber-50 dark:to-amber-950/30 border-emerald-200 dark:border-emerald-900/50 text-gray-900 dark:text-gray-100",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
